import { CSSProperties, ReactNode } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Link } from 'components/common';
import { CoursePicture } from '.';

import type { Course } from 'shared-types';

type Props = {
  course: Partial<
    Pick<Course, 'id' | 'languageVersions' | 'picturePosition' | 'slug'>
  > &
    Pick<Course, 'picture' | 'title'>;
} & (
  | { linkToCourseAdminPage?: false; linkAs: string }
  | { linkToCourseAdminPage?: boolean; linkAs?: never }
);

interface CommonProps {
  children?: ReactNode;
  iconTooltip?: string;
  linkHref?: string;
  onClick?: () => void;
  pictureStyle?: CSSProperties;
  preferredLanguageVersion?: string;
  style?: CSSProperties;
  titleStyle?: CSSProperties;
}

const CourseTitleWithInfo = ({
  children,
  course,
  iconTooltip,
  linkAs,
  linkHref,
  linkToCourseAdminPage,
  onClick,
  pictureStyle,
  preferredLanguageVersion,
  style,
  titleStyle
}: Props & CommonProps) => {
  const { theme } = useReduxState();

  let courseTitle = course.title;
  if (course) {
    const languageVersion = course.languageVersions?.[preferredLanguageVersion]
      ?.isPublished
      ? course.languageVersions?.[preferredLanguageVersion]
      : null;

    courseTitle = languageVersion?.courseTitle || courseTitle;
  }

  return (
    <div className="course" style={style}>
      <div
        className="picture"
        style={pictureStyle}
        {...(iconTooltip ? { 'data-tooltip': iconTooltip } : {})}
      >
        <CoursePicture
          position={course.picturePosition}
          size={480}
          url={course.picture}
        />
      </div>

      <div className="content">
        <div className="title" style={titleStyle}>
          <span className="sr-only">{iconTooltip} </span>

          {onClick ? (
            <button className="title-link" onClick={onClick} tabIndex={0}>
              {courseTitle}
            </button>
          ) : linkToCourseAdminPage || linkHref ? (
            <Link
              as={
                linkAs
                  ? linkAs
                  : linkToCourseAdminPage && course.slug
                  ? `/admin/courses/${course.slug}`
                  : undefined
              }
              href={linkHref || '/admin/courses/[courseSlug]'}
              linkColor="inherit"
              text={courseTitle}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                display: 'inline-block',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                outline: 0,
                padding: 0
              }}
            />
          ) : (
            courseTitle
          )}
        </div>

        {children ? <div className="info">{children}</div> : null}
      </div>

      <style jsx>{`
        .course {
          display: flex;
        }
        .picture {
          border-radius: 6px;
          flex-shrink: 0;
          margin-right: 10px;
          margin-top: 3px;
          overflow: hidden;
          position: relative;
          height: 32px;
          width: 32px;
        }
        .content {
          flex-grow: 1;
          min-width: 0;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.5rem;
          font-weight: 700;
        }
        .title-link {
          background-color: transparent;
          border: none;
          color: inherit;
          cursor: pointer;
          display: inline-block;
          font-family: inherit;
          font-size: inherit;
          font-weight: inherit;
          outline: 0;
          padding: 0;
          text-decoration: none;
        }
        .title-link:focus,
        .title-link:hover {
          text-decoration: underline;
        }
        .info {
          display: block;
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1.5;
        }
        @media (min-width: ${breakpoints.small}px) {
          .info {
            display: flex;
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseTitleWithInfo;
